import React, { useMemo } from 'react';
import {
  timeZoneOptions,
  zoneLabel,
  localPartsToInstant,
  instantToLocalParts,
  endOfLocalDay,
  shiftYmd,
} from '../../Utils/eventTime';
import { buildTimingPayload } from './eventForm';
import styles from './Styles/EventSettings.module.css';

// Ordering guard shared with Event.jsx's submit handler: registration opens
// <= start <= end <= sales close. Compares the actual instants
// `buildTimingPayload` will send (endsAt/expiresAt land at the END of their
// chosen minute, not the start — see eventForm.js) rather than re-deriving
// them here, so the two can't silently disagree. Returns null when the
// fields aren't all filled in yet (no error to show) or when the ordering
// holds.
export function validateEventTiming(value) {
  const {
    timezone,
    registrationYmd,
    registrationHm,
    startYmd,
    startHm,
    endYmd,
    endHm,
    expiresYmd,
    expiresHm,
  } = value || {};
  if (
    !timezone ||
    !registrationYmd ||
    !registrationHm ||
    !startYmd ||
    !startHm ||
    !endYmd ||
    !endHm ||
    !expiresYmd ||
    !expiresHm
  ) {
    return null;
  }
  const payload = buildTimingPayload(value);
  const registration = new Date(payload.registrationStartsAt).getTime();
  const start = new Date(payload.startsAt).getTime();
  const end = new Date(payload.endsAt).getTime();
  const expires = new Date(payload.expiresAt).getTime();
  if (registration > start) return 'Registration opens must be at or before the start.';
  if (start > end) return 'Start must be at or before the end.';
  if (end > expires) return 'Sales close must be at or after the end.';
  return null;
}

// Controlled section for the event create/edit form: Timing, Sales, Prices,
// Matching (SCRUM-334; registration opens SCRUM-449 follow-up). `value`
// carries every field below plus `salesWindowDays` (Defaults' pre-fill
// window, used only to auto-compute Sales close — never sent to the API),
// `expiresTouched` and `registrationTouched` (booleans — `true` when the
// corresponding field came from an already-saved event (edit mode) or a
// hand-typed edit, rather than a fresh auto-fill (create mode)). Event.jsx
// owns its initial value so it survives this component unmounting, e.g. on a
// failed submit's `loading ? <Loader/> : <form>` swap. The "Reset to
// default" action clears `expiresTouched` so end-date changes start
// re-filling Sales close again; there is no equivalent reset for
// Registration opens (product decision — it's a rarer manual override).
export default function EventSettings({ value, onChange }) {
  const patch = (fields) => onChange({ ...value, ...fields });

  // Sales close = end-of-local-day(end) + salesWindowDays, in the event's
  // zone. Pure — callers decide whether `expiresTouched` should block it.
  const computeSalesClose = (next) => {
    const { timezone, endYmd, endHm, salesWindowDays } = next;
    if (!timezone || !endYmd || !endHm) return null;
    if (salesWindowDays === '' || salesWindowDays == null || Number.isNaN(Number(salesWindowDays))) return null;
    const endInstant = localPartsToInstant(endYmd, endHm, timezone);
    const expiresInstant = endOfLocalDay(endInstant, timezone, Number(salesWindowDays));
    const { ymd, hm } = instantToLocalParts(expiresInstant, timezone);
    return { expiresYmd: ymd, expiresHm: hm };
  };

  const recomputeExpires = (next) => {
    if (next.expiresTouched) return {};
    return computeSalesClose(next) || {};
  };

  // Registration opens defaults to the same wall-clock time as the start,
  // one calendar day earlier (product decision, SCRUM-449 follow-up) — pure
  // calendar-day arithmetic, no timezone math needed beyond what start
  // already carries. Pure — callers decide whether `registrationTouched`
  // should block it.
  const computeRegistrationOpen = (next) => {
    const { startYmd, startHm } = next;
    if (!startYmd || !startHm) return null;
    return { registrationYmd: shiftYmd(startYmd, -1), registrationHm: startHm };
  };

  const recomputeRegistration = (next) => {
    if (next.registrationTouched) return {};
    return computeRegistrationOpen(next) || {};
  };

  // Timing changes (timezone/start/end) may re-fill Sales close and
  // Registration opens.
  const handleTimingChange = (fields) => {
    const next = { ...value, ...fields };
    onChange({ ...next, ...recomputeExpires(next), ...recomputeRegistration(next) });
  };

  const handleExpiresChange = (fields) => {
    patch({ ...fields, expiresTouched: true });
  };

  const handleRegistrationChange = (fields) => {
    patch({ ...fields, registrationTouched: true });
  };

  // Explicit override for an already-touched Sales close (edit mode, or after
  // a manual edit): recompute from the current end + salesWindowDays and drop
  // back into "auto" mode so subsequent end-date changes re-fill it again.
  const handleResetToDefault = () => {
    const computed = computeSalesClose(value);
    if (!computed) return;
    patch({ ...computed, expiresTouched: false });
  };

  const canResetToDefault = !!computeSalesClose(value);
  const timingError = validateEventTiming(value);
  const zones = useMemo(() => timeZoneOptions(), []);

  return (
    <div className={styles.settings}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Timing</h3>
        <label className={styles.field}>
          Timezone
          <select
            value={value.timezone}
            onChange={(e) => handleTimingChange({ timezone: e.target.value })}
          >
            {zones.map((tz) => (
              <option key={tz} value={tz}>
                {zoneLabel(tz)}
              </option>
            ))}
          </select>
        </label>
        <div className={styles.row}>
          <label className={styles.field}>
            Registration opens date
            <input
              type="date"
              value={value.registrationYmd}
              onChange={(e) => handleRegistrationChange({ registrationYmd: e.target.value })}
              required
            />
          </label>
          <label className={styles.field}>
            Registration opens time
            <input
              type="time"
              value={value.registrationHm}
              onChange={(e) => handleRegistrationChange({ registrationHm: e.target.value })}
              required
            />
          </label>
        </div>
        <div className={styles.hint}>
          When the event appears in the app and parents can join. Defaults to 1 day before the start.
        </div>
        <div className={styles.row}>
          <label className={styles.field}>
            Start date
            <input
              type="date"
              value={value.startYmd}
              onChange={(e) => handleTimingChange({ startYmd: e.target.value })}
              required
            />
          </label>
          <label className={styles.field}>
            Start time
            <input
              type="time"
              value={value.startHm}
              onChange={(e) => handleTimingChange({ startHm: e.target.value })}
              required
            />
          </label>
        </div>
        <div className={styles.row}>
          <label className={styles.field}>
            End date
            <input
              type="date"
              value={value.endYmd}
              onChange={(e) => handleTimingChange({ endYmd: e.target.value })}
              required
            />
          </label>
          <label className={styles.field}>
            End time
            <input
              type="time"
              value={value.endHm}
              onChange={(e) => handleTimingChange({ endHm: e.target.value })}
              required
            />
          </label>
        </div>
        {timingError && <div className={styles.error}>{timingError}</div>}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Sales</h3>
        <div className={styles.row}>
          <label className={styles.field}>
            Sales close date
            <input
              type="date"
              value={value.expiresYmd}
              onChange={(e) => handleExpiresChange({ expiresYmd: e.target.value })}
              required
            />
          </label>
          <label className={styles.field}>
            Sales close time
            <input
              type="time"
              value={value.expiresHm}
              onChange={(e) => handleExpiresChange({ expiresHm: e.target.value })}
              required
            />
          </label>
          <button
            type="button"
            className={styles.resetLink}
            onClick={handleResetToDefault}
            disabled={!canResetToDefault}
            title="Recompute from the current end date + Defaults' sales window, and let future end-date changes re-fill it again"
          >
            Reset to default (end + {value.salesWindowDays || 'N'} days)
          </button>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Prices</h3>
        <div className={styles.row}>
          <label className={styles.field}>
            Photo price ($)
            <input
              type="number"
              step="0.01"
              value={value.imagePrice}
              onChange={(e) => patch({ imagePrice: e.target.value })}
              required
            />
          </label>
          <label className={styles.field}>
            Album price ($)
            <input
              type="number"
              step="0.01"
              value={value.albumPrice}
              onChange={(e) => patch({ albumPrice: e.target.value })}
              required
            />
          </label>
          <label className={styles.field}>
            Survey discount (%)
            <input
              type="number"
              step="0.01"
              value={value.discountPercent}
              onChange={(e) => patch({ discountPercent: e.target.value })}
            />
          </label>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Matching</h3>
        <label className={styles.field}>
          Face-match threshold
          <input
            type="number"
            min="89"
            max="100"
            value={value.matchThreshold}
            onChange={(e) => patch({ matchThreshold: e.target.value })}
            required
          />
        </label>
        <div className={styles.hint}>
          auto matches at or above this are visible to parents; confirmed always, suppressed never
        </div>
      </section>
    </div>
  );
}
